import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { ArrowUp, Loader2, Check, Pencil, Trash2, Plus, ArrowRight, Paperclip, X, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';

/* ═══════════════════════════════════════════════════════════════
   INTAKE API HELPER — uses Base44 SDK's built-in function calling

   The SDK always wraps the edge function response in:
     { data: {...actual payload...}, status: 200, headers: {...} }
   We unwrap to `.data`. If `.data` is missing, something went wrong
   at the SDK layer — throw a clear error rather than silently
   returning the wrapper (which causes empty-bubble symptoms).
   ═══════════════════════════════════════════════════════════════ */
async function intakeApi(action, params = {}) {
  const raw = await base44.functions.invoke('intakeAI', { action, ...params });

  if (!raw || typeof raw !== 'object') {
    throw new Error('intakeAI returned no response');
  }
  const result = raw.data;
  if (!result || typeof result !== 'object') {
    throw new Error('intakeAI returned no payload (raw keys: ' + Object.keys(raw).join(',') + ')');
  }
  if (result.error) throw new Error(result.error);
  return result;
}

/* ═══════════════════════════════════════════════════════════════
   IMAGE HELPERS
   readFileAsBase64 — uses FileReader (no canvas, no DOM Image).
   Simple and reliable in all contexts including Radix dialogs.
   ═══════════════════════════════════════════════════════════════ */
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:image/png;base64,XXXXX" — strip the prefix
      const b64 = reader.result.split(',')[1];
      resolve(b64);
    };
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function getMediaType(file) {
  if (file.type === 'image/png') return 'image/png';
  if (file.type === 'image/gif') return 'image/gif';
  if (file.type === 'image/webp') return 'image/webp';
  return 'image/jpeg'; // default for screenshot pastes
}

/* ═══════════════════════════════════════════════════════════════
   MESSAGE — REEF speaks as a cyan hairline; user bubble is indigo
   ═══════════════════════════════════════════════════════════════ */
function ChatMessage({ role, content, attachments }) {
  if (role === 'assistant') {
    return (
      <div className="intake-fade-in mb-4 max-w-full">
        <div
          className="w-0.5 h-3.5 bg-cyan-400 mb-1.5"
          style={{ boxShadow: '0 0 8px rgba(34,211,238,0.5)' }}
        />
        <div
          className="text-[15px] leading-[1.55] text-slate-100 whitespace-pre-wrap"
          style={{ textWrap: 'pretty' }}
        >
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="intake-fade-in flex justify-end mb-4">
      <div className="max-w-[85%] flex flex-col items-end gap-1.5">
        {/* Attachment thumbnails in the message bubble */}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 justify-end">
            {attachments.map((att, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-indigo-500/30 bg-slate-800">
                {att.url ? (
                  <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-slate-500" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div
          className="px-3.5 py-2.5 text-[15px] leading-[1.5] text-slate-100 whitespace-pre-wrap bg-indigo-500/15 border border-indigo-500/30"
          style={{ borderRadius: '10px 10px 2px 10px' }}
        >
          {content}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   THINKING STATUS — scan line, not bouncing dots
   ═══════════════════════════════════════════════════════════════ */
function ThinkingStatus() {
  return (
    <div className="intake-fade-in flex items-center gap-2.5 py-1 px-0.5 mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500">
      <span>reading</span>
      <span className="intake-status-bar relative flex-1 h-px overflow-hidden bg-white/[0.04] rounded-[1px]" />
      <span>gate 1 · gate 2</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EMPTY MEMBRANE — three concentric rings, dissolves when first
   message arrives. Cyan glow; calm rotation / breathing motion.
   ═══════════════════════════════════════════════════════════════ */
function EmptyMembrane() {
  return (
    <div
      aria-hidden="true"
      className="intake-membrane absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
    >
      <svg
        className="absolute opacity-95"
        style={{ width: 340, height: 340, maxWidth: '80%', maxHeight: '80%' }}
        viewBox="-160 -160 320 320"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="intakeMembraneGlow" cx="0" cy="0" r="0.5">
            <stop offset="0%" stopColor="rgba(34,211,238,0.22)" />
            <stop offset="60%" stopColor="rgba(34,211,238,0.04)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0)" />
          </radialGradient>
          <linearGradient id="intakeMembraneStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(99,102,241,0.55)" />
            <stop offset="50%" stopColor="rgba(34,211,238,0.55)" />
            <stop offset="100%" stopColor="rgba(99,102,241,0)" />
          </linearGradient>
        </defs>
        <circle cx="0" cy="0" r="140" fill="url(#intakeMembraneGlow)" />
        <circle className="intake-ring-1" cx="0" cy="0" r="120" fill="none" stroke="url(#intakeMembraneStroke)" strokeWidth="0.75" />
        <circle className="intake-ring-2" cx="0" cy="0" r="82" fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="0.5" strokeDasharray="1 4" />
        <circle className="intake-ring-3" cx="0" cy="0" r="44" fill="none" stroke="rgba(34,211,238,0.35)" strokeWidth="0.75" />
        <circle cx="0" cy="0" r="2" fill="#22d3ee" style={{ filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.8))' }} />
      </svg>
      <div className="relative flex flex-col items-center gap-1.5 text-center">
        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cyan-300 opacity-80">REEF</div>
        <div
          className="font-sans text-[22px] font-medium text-slate-100 leading-[1.2]"
          style={{ letterSpacing: '-0.015em', textShadow: '0 0 24px rgba(34,211,238,0.15)' }}
        >
          what's going on?
        </div>
        <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500 mt-0.5">
          start anywhere · a sentence is enough
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PREVIEW CARD — receipt-style: gradient surface, mono badges,
   inline edit/remove
   ═══════════════════════════════════════════════════════════════ */
function PreviewCard({ card, index, onEdit, onRemove, isOnly }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(card.title || '');
  const [editDescription, setEditDescription] = useState(card.description || '');

  const typePill = {
    bug:         'bg-rose-500/12 text-rose-300 border-rose-500/35',
    feature:     'bg-indigo-500/14 text-indigo-300 border-indigo-500/38',
    improvement: 'bg-cyan-500/12 text-cyan-300 border-cyan-500/35',
    question:    'bg-slate-400/10 text-slate-300 border-slate-400/28',
    org:         'bg-amber-500/12 text-amber-300 border-amber-500/40'
  };

  const priorityColor = {
    critical: 'text-rose-400',
    high:     'text-amber-300',
    medium:   'text-slate-300',
    low:      'text-slate-500'
  };

  if (editing) {
    return (
      <div
        className="intake-receipt-in rounded-xl p-4 mb-2 border border-white/[0.08]"
        style={{ background: 'linear-gradient(145deg, rgba(30,41,59,0.55) 0%, rgba(15,23,42,0.7) 100%)' }}
      >
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="w-full bg-slate-950/60 border border-white/10 rounded px-3 py-1.5 text-sm text-slate-100 mb-2 focus:outline-none focus:border-cyan-400/40"
          placeholder="Title"
        />
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          rows={3}
          className="w-full bg-slate-950/60 border border-white/10 rounded px-3 py-1.5 text-sm text-slate-200 resize-none mb-2 focus:outline-none focus:border-cyan-400/40"
          placeholder="Description"
        />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs text-slate-400">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => { onEdit(index, { ...card, title: editTitle, description: editDescription }); setEditing(false); }}
            className="h-7 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-200"
          >
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="intake-receipt-in group rounded-xl border border-white/[0.08] p-[18px] mb-2 flex flex-col gap-3"
      style={{ background: 'linear-gradient(145deg, rgba(30,41,59,0.55) 0%, rgba(15,23,42,0.7) 100%)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {card.type && (
            <span className={`font-mono text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-0.5 rounded border ${typePill[card.type] || typePill.bug}`}>
              {card.type}
            </span>
          )}
          {card.priority && (
            <span className={`font-mono text-[10px] uppercase tracking-[0.16em] ${priorityColor[card.priority] || priorityColor.medium}`}>
              {card.priority}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-white/[0.06] text-slate-400">
            <Pencil className="w-3 h-3" />
          </button>
          {!isOnly && (
            <button onClick={() => onRemove(index)} className="p-1 rounded hover:bg-white/[0.06] text-slate-400">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <div
        className="text-[16px] font-semibold text-slate-100 leading-[1.35]"
        style={{ letterSpacing: '-0.01em' }}
      >
        {card.title || 'Untitled'}
      </div>

      {card.description && (
        <div
          className="text-[14px] text-slate-300 leading-[1.55]"
          style={{ textWrap: 'pretty' }}
        >
          {card.description}
        </div>
      )}

      {card.source_app && (
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-500">
            {card.source_app}
          </span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ATTACHMENT THUMBNAIL STRIP — shown above the send bar
   ═══════════════════════════════════════════════════════════════ */
function AttachmentStrip({ attachments, onRemove, uploading }) {
  if (attachments.length === 0 && !uploading) return null;
  return (
    <div className="flex flex-wrap gap-2 px-[22px] pt-3 pb-0">
      {attachments.map((att, i) => (
        <div key={i} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-white/10 bg-slate-800/60 shrink-0">
          {att.previewUrl ? (
            <img src={att.previewUrl} alt={att.filename} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Image className="w-5 h-5 text-slate-500" />
            </div>
          )}
          <button
            onClick={() => onRemove(i)}
            className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-slate-900/80 text-slate-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      ))}
      {uploading && (
        <div className="w-14 h-14 rounded-lg border border-white/10 bg-slate-800/60 flex items-center justify-center shrink-0">
          <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   INTAKE CHAT — main component

   SESSION-RESUME ARCHITECTURE:
   This component remounts every time the Dialog opens (Radix Dialog
   unmounts its content on close). Session state is preserved on the
   SERVER — the `IntakeSession` entity holds session_id, messages, etc.
   The parent (FeedbackButton) retains the session_id across close/open
   cycles and passes it back in as `resumeSessionId`.

   On mount:
     - If `resumeSessionId` prop is provided → call `resume` to rehydrate
     - Otherwise → call `start` for a fresh session

   Whenever we allocate a new session_id (on `start`), we bubble it up
   via `onSessionCreated` so the parent can remember it for next time.

   ATTACHMENT ARCHITECTURE:
   Attachments are handled entirely client-side until the next message send:
   1. User pastes / picks a file → resized to base64 + uploaded to Base44 CDN
   2. Stored in pendingAttachments state: [{ filename, url, base64, mediaType, previewUrl }]
   3. On send: base64 + mediaType passed to intakeAI 'message' action alongside text
   4. REEF receives image content blocks → vision API → sees the screenshot
   5. After send: pendingAttachments cleared; stored URLs attached to session messages
   ═══════════════════════════════════════════════════════════════ */
export default function IntakeChat({
  onClose,
  onSwitchToForm,
  sourceApp,
  resumeSessionId,
  onSessionCreated
}) {
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  /* ── State ── */
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [cards, setCards] = useState(null);
  const [turnCount, setTurnCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submittedIds, setSubmittedIds] = useState([]);
  const [error, setError] = useState(null);

  /* Attachment state — cleared after each send */
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  /* ── Auto-scroll ── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, cards]);

  /* ── Focus input on mount ── */
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  /* ── Init session: resume if we have a prior session_id, else start fresh ──
     Empty deps: runs exactly once per mount. resumeSessionId/sourceApp are
     captured in the closure; we don't want this firing again if they change. */
  useEffect(() => {
    let cancelled = false;

    async function startFresh() {
      try {
        setIsTyping(true);
        const result = await intakeApi('start', { source_app: sourceApp || 'COMPASS' });
        if (cancelled) return;
        setSessionId(result.session_id);
        setMessages([{ role: 'assistant', content: result.greeting }]);
        if (onSessionCreated) onSessionCreated(result.session_id);
      } catch (err) {
        if (!cancelled) setError('Failed to start session: ' + err.message);
      } finally {
        if (!cancelled) setIsTyping(false);
      }
    }

    async function resumeSession(sid) {
      try {
        setIsTyping(true);
        const result = await intakeApi('resume', { session_id: sid });
        if (cancelled) return;

        if (!result.session_id || result.status === 'not_found' || result.status === 'expired') {
          await startFresh();
          return;
        }

        setSessionId(result.session_id);
        setMessages(Array.isArray(result.messages) ? result.messages : []);
        setCards(result.cards || null);
        setTurnCount(result.turn_count || 0);

        if (result.status === 'submitted') {
          setSubmitted(true);
          setSubmittedIds(result.feedback_ids || []);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('[IntakeChat] resume failed, starting fresh:', err.message);
        await startFresh();
      } finally {
        if (!cancelled) setIsTyping(false);
      }
    }

    if (resumeSessionId) {
      resumeSession(resumeSessionId);
    } else {
      startFresh();
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Process a file (from picker or paste) ── */
  const processFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (pendingAttachments.length >= 3) {
      setError('Maximum 3 images per message.');
      return;
    }

    setUploadingAttachment(true);
    try {
      /* Read base64 first — FileReader is reliable in all dialog contexts */
      const base64 = await readFileAsBase64(file);
      const mediaType = getMediaType(file);

      /* Local preview URL for thumbnail */
      const previewUrl = URL.createObjectURL(file);

      /* Upload to Base44 CDN for persistent URL on the submitted card */
      const { file_url } = await base44.integrations.Core.UploadFile({ file });

      setPendingAttachments(prev => [...prev, {
        filename: file.name || `screenshot-${Date.now()}.png`,
        url: file_url,
        base64,
        mediaType,
        previewUrl
      }]);
    } catch (err) {
      console.error('[IntakeChat] processFile error:', err);
      setError('Upload failed: ' + err.message);
    } finally {
      setUploadingAttachment(false);
    }
  }, [pendingAttachments.length]);

  /* ── Paste handler — catches clipboard images ── */
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await processFile(file);
        return; // one image per paste
      }
    }
    /* Not an image paste — let default text paste through */
  }, [processFile]);

  /* ── File picker handler ── */
  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files.slice(0, 3 - pendingAttachments.length)) {
      await processFile(file);
    }
    e.target.value = '';
  }, [processFile, pendingAttachments.length]);

  /* ── Remove pending attachment ── */
  const handleRemoveAttachment = useCallback((index) => {
    setPendingAttachments(prev => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  /* ── Send message ── */
  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if ((!text && pendingAttachments.length === 0) || !sessionId || isTyping) return;

    const attachmentsToSend = [...pendingAttachments];
    setInputValue('');
    setPendingAttachments([]);
    setError(null);

    const userMsg = {
      role: 'user',
      content: text || '(image attached)',
      attachments: attachmentsToSend.map(a => ({ filename: a.filename, url: a.url }))
    };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    try {
      const result = await intakeApi('message', {
        session_id: sessionId,
        message: text || '(see attached image)',
        /* Pass vision data — base64 + mediaType per attachment */
        attachments: attachmentsToSend.map(a => ({
          filename: a.filename,
          url: a.url,
          base64: a.base64,
          media_type: a.mediaType
        }))
      });
      setMessages(prev => [...prev, { role: 'assistant', content: result.reply_text }]);
      setTurnCount(result.turn_count || 0);

      if (result.cards) {
        setCards(result.cards);
      }
    } catch (err) {
      setError('Failed to send message: ' + err.message);
      setMessages(prev => prev.slice(0, -1));
      setInputValue(text);
      setPendingAttachments(attachmentsToSend);
    } finally {
      setIsTyping(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [inputValue, pendingAttachments, sessionId, isTyping]);

  /* ── Submit ── */
  const handleSubmit = useCallback(async () => {
    if (!sessionId || !cards || cards.length === 0) return;
    setIsTyping(true);
    setError(null);

    try {
      const result = await intakeApi('submit', { session_id: sessionId, cards_override: cards });
      setSubmitted(true);
      setSubmittedIds(result.feedback_ids || []);
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['myFeedback'] });
    } catch (err) {
      setError('Failed to submit: ' + err.message);
    } finally {
      setIsTyping(false);
    }
  }, [sessionId, cards, queryClient]);

  /* ── Edit card ── */
  const handleEditCard = (index, updated) => {
    setCards(prev => prev.map((c, i) => i === index ? updated : c));
  };

  /* ── Remove card ── */
  const handleRemoveCard = (index) => {
    setCards(prev => prev.filter((_, i) => i !== index));
  };

  /* ── Submit another ── */
  const handleSubmitAnother = () => {
    setSessionId(null);
    setMessages([]);
    setInputValue('');
    setPendingAttachments([]);
    setCards(null);
    setTurnCount(0);
    setSubmitted(false);
    setSubmittedIds([]);
    setError(null);
    (async () => {
      try {
        setIsTyping(true);
        const result = await intakeApi('start', { source_app: sourceApp || 'COMPASS' });
        setSessionId(result.session_id);
        setMessages([{ role: 'assistant', content: result.greeting }]);
        if (onSessionCreated) onSessionCreated(result.session_id);
      } catch (err) {
        setError('Failed to start new session: ' + err.message);
      } finally {
        setIsTyping(false);
      }
    })();
  };

  /* ── Key handler ── */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = messages.length === 0;
  const canSend = (inputValue.trim() || pendingAttachments.length > 0) && !isTyping && !!sessionId;

  /* ═══ RENDER: Submitted state ═══ */
  if (submitted) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/35 flex items-center justify-center mb-4">
            <Check className="w-6 h-6 text-emerald-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-100 mb-1">
            {submittedIds.length === 1 ? 'Item submitted' : `${submittedIds.length} items submitted`}
          </h3>
          <p className="text-sm text-slate-400 mb-6 max-w-sm" style={{ textWrap: 'pretty' }}>
            REEF has structured and submitted your feedback. It'll flow through the pipeline from here.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={handleSubmitAnother}
              variant="outline"
              className="border-white/10 text-slate-300 hover:bg-white/[0.04] hover:text-slate-100"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Submit another
            </Button>
            <Button
              onClick={onClose}
              className="bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-200"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ═══ RENDER: Main chat ═══ */
  return (
    <div className="flex flex-col h-full">
      {/* Header — mono REEF tag + switch-to-form link */}
      <div className="px-4 py-3.5 border-b border-white/[0.05] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          <span
            className="w-[7px] h-[7px] rounded-full bg-cyan-400"
            style={{ boxShadow: '0 0 10px rgba(34,211,238,0.55)' }}
          />
          <span>REEF</span>
        </div>
        <button
          onClick={onSwitchToForm}
          className="text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500 hover:text-cyan-300 transition-colors flex items-center gap-1.5"
        >
          Standard form <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-[22px] pt-5 relative min-h-[340px]">
        {isEmpty && <EmptyMembrane />}

        {messages.map((msg, i) => (
          <ChatMessage key={i} role={msg.role} content={msg.content} attachments={msg.attachments} />
        ))}

        {isTyping && !isEmpty && <ThinkingStatus />}

        {/* Preview cards */}
        {cards && cards.length > 0 && !submitted && (
          <div className="mt-4 mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">
              {cards.length === 1 ? 'Ready to submit' : `${cards.length} items to submit`}
            </p>
            {cards.map((card, i) => (
              <PreviewCard
                key={i}
                card={card}
                index={i}
                onEdit={handleEditCard}
                onRemove={handleRemoveCard}
                isOnly={cards.length === 1}
              />
            ))}
            <div className="flex gap-2 mt-3">
              <Button
                onClick={handleSubmit}
                disabled={isTyping}
                className="flex-1 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-200 h-9 text-sm"
              >
                {isTyping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1.5" />
                    Submit {cards.length > 1 ? `${cards.length} items` : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2 mb-2">
            <p className="text-xs text-rose-300">{error}</p>
            <button onClick={() => setError(null)} className="text-[10px] text-rose-400 underline mt-1">
              Dismiss
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Attachment strip — between messages and input */}
      {!cards && (
        <AttachmentStrip
          attachments={pendingAttachments}
          onRemove={handleRemoveAttachment}
          uploading={uploadingAttachment}
        />
      )}

      {/* Input area */}
      {!cards && (
        <div className="px-[22px] pb-[18px] pt-3 shrink-0">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />

          <div
            className="relative rounded-[10px] border border-white/[0.08] bg-slate-950/55 transition-[border-color,box-shadow] duration-200 focus-within:border-cyan-400/40 focus-within:[box-shadow:0_0_0_3px_rgba(34,211,238,0.08)]"
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              aria-label="Describe what's going on"
              data-testid="intake-chat-textarea"
              placeholder={isEmpty ? '' : 'add to this…'}
              rows={1}
              className="w-full bg-transparent border-0 outline-none resize-none text-[15px] leading-[1.5] text-slate-100 placeholder:text-slate-500 px-4 py-3.5 pr-[88px] min-h-[48px] max-h-[160px]"
              style={{ height: 'auto', overflow: 'hidden' }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
              }}
              disabled={isTyping || !sessionId}
            />
            {/* Paperclip button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isTyping || !sessionId || pendingAttachments.length >= 3}
              className="absolute right-10 bottom-2 w-8 h-8 rounded-lg text-slate-500 grid place-items-center transition-all duration-150 hover:text-slate-300 hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-default"
              title="Attach image (or paste with Ctrl+V)"
            >
              <Paperclip className="w-[14px] h-[14px]" />
            </button>
            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="absolute right-2 bottom-2 w-8 h-8 rounded-lg bg-cyan-500/16 border border-cyan-400/34 text-cyan-300 grid place-items-center transition-all duration-150 hover:bg-cyan-500/26 hover:text-white disabled:opacity-40 disabled:cursor-default disabled:hover:bg-cyan-500/16 disabled:hover:text-cyan-300"
            >
              {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-[14px] h-[14px]" />}
            </button>
            {isTyping && (
              <div
                className="intake-scan-bar absolute left-0 right-0 -bottom-px h-px overflow-hidden pointer-events-none"
                style={{ borderRadius: '0 0 10px 10px' }}
              />
            )}
          </div>
          <div className="flex items-center justify-between pt-1.5 px-0.5 font-mono text-[10px] tracking-[0.12em] uppercase text-slate-500">
            <span>↵ send · shift+↵ newline · ⌘V paste image</span>
            {sourceApp && (
              <span className="inline-flex items-center gap-1.5 text-slate-500">
                <span className="w-1 h-1 rounded-full bg-current opacity-50" />
                {sourceApp}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

